import React, { useEffect, useRef, useState } from "react";
import { Content, ContentSortMode, ContentSortOrder } from "../../../common/types";
import useDynamicRequest from "../../../common/hooks/useDynamicRequest";
import { useSelector } from "react-redux";
import { contentState } from "../../../store/slices/content";
import ContentGrid from "./ContentGrid";
import ContentList from "./ContentList";
import { Box, Button, Center, Spinner } from "@chakra-ui/react";
import { ErrorText, H3Gray } from "../../../common/components/Texts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortDown } from "@fortawesome/free-solid-svg-icons";

type DisplayModes = "grid" | "list";

interface OwnProps {
    displayMode: DisplayModes;
    subjectId: string;
    loadFavorites?: boolean;
}

export interface ContentDisplayProps {
    content: Content[];
    loadFavorites?: boolean;
    onLoadMoreContent: () => void;
    showLoadMore: boolean;
}

const ContentDisplay = ({ displayMode, subjectId, loadFavorites }: OwnProps) => {
    const limit = 50;
    const skip = useRef(0);
    const [sortBy, setSortBy] = useState<ContentSortMode>("latest");
    const [sortOrder, setSortOrder] = useState<ContentSortOrder>("desc");

    const getSearchParams = () => {
        const searchParams = new URLSearchParams({
            subject: subjectId,
            skip: "0",
            limit: limit.toString(),
            sortBy: sortBy,
            order: sortOrder,
            favorites: "false",
        });

        if (loadFavorites) {
            searchParams.set("favorites", "true");
        }

        return searchParams;
    }


    const [{ data, hasError, isLoading }, callGetContent] =
        useDynamicRequest<Content[]>();
    const [content, setContent] = useState<Content[]>([]);
    const { content: stateContent } = useSelector(contentState);
    const [showLoadMore, setShowLoadMore] = useState(false);

    useEffect(() => {
        callGetContent(
            "GET",
            `/content?mode=subject&${getSearchParams().toString()}`
        );
    }, [sortBy, sortOrder]);

    useEffect(() => {
        if (data && data.length > 0) {
            setContent(data);
        }

        if (data && data.length > limit) {
            setShowLoadMore(true);
        }
    }, [data]);

    useEffect(() => {
        if (stateContent.length > 0) {
            setContent([...content, ...stateContent]);
        }
    }, [stateContent]);

    const onLoadMoreContent = () => {
        const currentSkip = (skip.current + 1) * limit;
        const searchParams = getSearchParams();
        searchParams.set("skip", currentSkip.toString());

        callGetContent(
            "GET",
            `/content?mode=subject&${searchParams.toString()}`
        );
        skip.current += 1;
    };

    return <>
        {isLoading && <Spinner />}
        {!isLoading && hasError && (
            <ErrorText text="Ha ocurrido un error. Por favor intenta nuevamente." />
        )}
        {!content.length && !loadFavorites && (
            <H3Gray text="No hay Contenido, crea uno 👆🏽" />
        )}
        <Box marginBottom={10}>
            {content.length > 0 && displayMode === "grid" &&
                <ContentGrid content={content}
                />}
            {content.length > 0 && displayMode === "list" &&
                <ContentList content={content} sortBy={sortBy} setSortBy={setSortBy} sortOrder={sortOrder} setSortOrder={setSortOrder} />}
            {showLoadMore && (
                <Center>
                    <Button
                        variant="ghost"
                        colorScheme="blackAlpha"
                        rightIcon={
                            <FontAwesomeIcon icon={faSortDown} />
                        }
                        onClick={onLoadMoreContent}
                    >
                        {loadFavorites ? "Cargar más Contenido Favorito" : "Cargar más Contenido"}
                    </Button>
                </Center>
            )}
        </Box>
    </>;
};

export default ContentDisplay;
